import React from "react";
import { Link } from "react-router-dom";

/* ------------------------------------------------------------------
   Page layout primitives shared by every route.

   <PageShell>  — the centered content column every page sits in.
   <PageHeader> — the title block at the top of a page: optional icon tile,
                  eyebrow (usually the nav section; `eyebrowTo` makes it a
                  link back to that section's hub, e.g. "/admin"), title,
                  description, and right-aligned actions that wrap under the
                  title on phones.
   <SectionHeader> — a smaller heading for a block within a page.

   They are plain Tailwind + the .app-* helpers in index.css, so they follow
   all four themes (classic/glass × light/dark) without any props.
------------------------------------------------------------------ */

const WIDTHS = {
  sm: "max-w-2xl",
  md: "max-w-4xl",
  lg: "max-w-5xl",
  xl: "max-w-6xl",
  "2xl": "max-w-7xl",
  full: "max-w-none",
};

export const PageShell = ({ size = "xl", className = "", children }) => (
  <div
    className={`mx-auto w-full ${WIDTHS[size] || WIDTHS.xl} px-4 py-6 sm:px-6 lg:py-8 ${className}`}
  >
    {children}
  </div>
);

// Icon tile tints — literal strings so Tailwind keeps them.
const TONES = {
  sky: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
  indigo: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300",
  violet: "bg-violet-500/15 text-violet-600 dark:text-violet-300",
  emerald: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  teal: "bg-teal-500/15 text-teal-600 dark:text-teal-300",
  amber: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
  orange: "bg-orange-500/15 text-orange-600 dark:text-orange-300",
  rose: "bg-rose-500/15 text-rose-600 dark:text-rose-300",
  slate: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
};

/** Icon in a tinted rounded tile. `icon` is a component (e.g. an MUI icon). */
export const IconTile = ({ icon: Icon, tone = "sky", size = "md", className = "" }) => {
  if (!Icon) return null;
  const dims =
    size === "lg" ? "h-12 w-12 rounded-2xl" : size === "sm" ? "h-9 w-9 rounded-xl" : "";
  return (
    <span className={`app-icon-tile ${dims} ${TONES[tone] || TONES.sky} ${className}`}>
      <Icon fontSize={size === "sm" ? "small" : "medium"} />
    </span>
  );
};

/**
 * Page title block.
 *
 *   <PageHeader
 *     icon={BuildIcon} tone="amber"
 *     eyebrow="Windows & Doors"
 *     title="Service Requests"
 *     description="Assign technicians and track every repair."
 *     actions={<Button variant="contained">New request</Button>}
 *   />
 */
export const PageHeader = ({
  icon,
  tone = "sky",
  eyebrow,
  eyebrowTo,
  title,
  description,
  actions,
  children,
  className = "",
}) => (
  <header className={`mb-6 ${className}`}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-4">
        {icon && <IconTile icon={icon} tone={tone} size="lg" />}
        <div className="min-w-0">
          {eyebrow && (
            <p className="app-eyebrow mb-1">
              {eyebrowTo ? (
                <Link
                  to={eyebrowTo}
                  className="rounded transition-colors hover:text-[color:var(--app-accent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                >
                  {eyebrow}
                </Link>
              ) : (
                eyebrow
              )}
            </p>
          )}
          <h1 className="text-2xl font-bold tracking-tight text-[color:var(--app-text)] sm:text-[1.75rem] sm:leading-9">
            {title}
          </h1>
          {description && (
            <p className="app-muted mt-1 max-w-2xl text-sm leading-relaxed sm:text-[0.95rem]">
              {description}
            </p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
    {children}
  </header>
);

/** Heading for a block inside a page. */
export const SectionHeader = ({ title, description, actions, className = "" }) => (
  <div className={`mb-3 flex flex-wrap items-end justify-between gap-3 ${className}`}>
    <div className="min-w-0">
      <h2 className="text-lg font-semibold tracking-tight text-[color:var(--app-text)]">
        {title}
      </h2>
      {description && <p className="app-muted text-sm">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

/** A raised panel. Same as `className="app-card rounded-2xl border"`. */
export const Card = ({ as: Tag = "div", padded = true, className = "", children, ...rest }) => (
  <Tag
    className={`app-card rounded-2xl border ${padded ? "p-5" : ""} ${className}`}
    {...rest}
  >
    {children}
  </Tag>
);
